<template>
  <section class="invoice-preview-wrapper">
    <b-card
      no-body
      class="compact-card main-padding"
    >
      <div class="compact-container">
        <b-row class="compact-row">
          <b-col
            md="12"
            class="d-flex justify-content-between align-items-center compact-header"
          >
            <h2 class="mb-0 compact-title">
              {{ $t("accounting.approved-invoices") }}
            </h2>
            <!-- View Toggle Buttons -->
            <div class="view-toggle-buttons">
              <b-button-group size="sm">
                <b-button
                  :variant="viewMode === 'list' ? 'primary' : 'outline-primary'"
                  size="sm"
                  @click="viewMode = 'list'"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="14"
                  />
                  {{ $t("common.table") }}
                </b-button>
                <b-button
                  :variant="viewMode === 'card' ? 'primary' : 'outline-primary'"
                  size="sm"
                  @click="viewMode = 'card'"
                >
                  <feather-icon
                    icon="GridIcon"
                    size="14"
                  />
                  Card
                </b-button>
              </b-button-group>
              <b-button
                variant="success"
                size="sm"
                class="ml-1"
                :disabled="selectedInvoiceIds.length === 0"
                @click="openBulkProcessModal"
              >
                <feather-icon
                  icon="CheckSquareIcon"
                  size="14"
                />
                Bulk Process ({{ selectedInvoiceIds.length }})
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row class="compact-row compact-filters">
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.from-date')"
              label-for="date"
              class="compact-form-group"
            >
              <datepicker
                id="date"
                v-model="selectedDate"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2 compact-input"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.type')"
              label-for="type-filter"
              class="compact-form-group"
            >
              <v-select
                id="type-filter"
                v-model="selectedType"
                :options="invoiceTypeOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="true"
                :searchable="true"
                label="label"
                :reduce="(option) => option.value"
                placeholder="All Types"
                class="compact-input"
              />
            </b-form-group>
          </b-col>

          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.invoice-number')"
              label-for="invoice-number-filter"
              class="compact-form-group"
            >
              <b-form-input
                id="invoice-number-filter"
                v-model="invoiceNumberFilter"
                :placeholder="$t('common.invoice-number')"
                class="compact-input"
                @keyup.enter="getApprovedInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('reception.patient-name')"
              label-for="patient-name-filter"
              class="compact-form-group"
            >
              <b-form-input
                id="patient-name-filter"
                v-model="patientNameFilter"
                :placeholder="$t('reception.patient-name')"
                class="compact-input"
                @keyup.enter="getApprovedInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.doctor-name')"
              label-for="doctor-filter"
              class="compact-form-group"
            >
              <AutoComplete
                id="doctor-filter"
                v-model="selectedDoctor"
                endpoint="doctor"
                param-query="search"
                text-title="name"
                :serializer="(val) => val.id"
                :placeholder="$t('doctor-filter')"
                class="compact-input"
                :clearable="true"
                :autocomplete-delay="300"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              label="نوع الخدمة"
              label-for="service-type-filter"
              class="compact-form-group"
            >
              <v-select
                id="service-type-filter"
                v-model="filterServiceType"
                :options="serviceTypeFilterOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="true"
                :searchable="true"
                label="text"
                :reduce="(option) => option.value"
                placeholder="نوع الخدمة"
                class="compact-input"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.approved_at_from')"
              label-for="from-approved-at"
              class="compact-form-group"
            >
              <datepicker
                id="from-approved-at"
                v-model="fromApprovedAt"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2 compact-input"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="3"
            md="4"
            cols="12"
            class="compact-col"
          >
            <b-form-group
              :label="$t('common.approved_at_to')"
              label-for="to-approved-at"
              class="compact-form-group"
            >
              <datepicker
                id="to-approved-at"
                v-model="toApprovedAt"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2 compact-input"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Summary Cards -->
        <b-row
          v-if="sums && !loading"
          class="compact-summary"
        >
          <b-col
            md="3"
            cols="12"
            class="compact-col"
          >
            <b-card class="text-center bg-light compact-summary-card">
              <h6 class="text-muted mb-1 compact-summary-label">
                {{ $t("common.total") }}
              </h6>
              <h4 class="text-primary mb-0 compact-summary-value">
                {{ formatIQDCurrency(sums.total_amount) }}
              </h4>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="compact-col"
          >
            <b-card class="text-center bg-light compact-summary-card">
              <h6 class="text-muted mb-1 compact-summary-label">
                {{ $t("common.discount") }}
              </h6>
              <h4 class="text-warning mb-0 compact-summary-value">
                {{ formatIQDCurrency(sums.total_discount) }}
              </h4>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="compact-col"
          >
            <b-card class="text-center bg-light compact-summary-card">
              <h6 class="text-muted mb-1 compact-summary-label">
                {{ $t("common.net-amount") }}
              </h6>
              <h4 class="text-success mb-0 compact-summary-value">
                {{ formatIQDCurrency(sums.net_amount) }}
              </h4>
            </b-card>
          </b-col>
          <b-col
            md="3"
            cols="12"
            class="compact-col"
          >
            <b-card class="text-center bg-light compact-summary-card">
              <h6 class="text-muted mb-1 compact-summary-label">
                {{ $t("common.count") }}
              </h6>
              <h4 class="text-info mb-0 compact-summary-value">
                {{ sums.count }}
              </h4>
            </b-card>
          </b-col>
        </b-row>

        <b-row class="compact-content-row">
          <b-col>
            <!-- List View -->
            <b-table
              v-if="!loading && viewMode === 'list'"
              :items="invoicesData"
              responsive
              :fields="tableColumns"
              primary-key="id"
              class="position-relative compact-table"
              show-empty
              empty-text="No matching invoices found"
            >
              <template #head(select)>
                <b-form-checkbox
                  :checked="allDisplayedSelected"
                  :indeterminate="someDisplayedSelected"
                  @change="toggleSelectAllDisplayed"
                />
              </template>
              <template #cell(select)="data">
                <b-form-checkbox
                  :checked="isInvoiceSelected(data.item)"
                  @change="
                    (checked) => toggleInvoiceSelection(data.item, checked)
                  "
                />
              </template>
              <template #cell(invoice_no)="data">
                <span class="font-weight-bold">{{
                  data.item.invoice_no || "-"
                }}</span>
              </template>
              <template #cell(type)="data">
                <div>
                  <span class="text-nowrap">{{
                    formatInvoiceType(data.item.type)
                  }}</span>
                  <div
                    v-if="
                      getInvoiceServiceTitle(data.item) ||
                        getInvoiceServiceTypeName(data.item)
                    "
                    class="mt-1"
                  >
                    <span v-if="getInvoiceServiceTitle(data.item)">{{
                      getInvoiceServiceTitle(data.item)
                    }}</span>
                    <span
                      v-if="
                        getInvoiceServiceTitle(data.item) &&
                          getInvoiceServiceTypeName(data.item)
                      "
                    >
                      ·
                    </span>
                    <span v-if="getInvoiceServiceTypeName(data.item)">{{
                      getInvoiceServiceTypeName(data.item)
                    }}</span>
                  </div>
                </div>
              </template>
              <template #cell(patientName)="data">
                <span>{{
                  getNestedProperty(data.item, "patient_card.patient.name", "-")
                }}</span>
              </template>
              <template #cell(itemTitle)="data">
                <span>{{
                  getNestedProperty(data.item, "service.title") ||
                    getNestedProperty(data.item, "surgery.title") ||
                    "-"
                }}</span>
              </template>
              <template #cell(amount)="data">
                <span class="font-weight-bold">{{
                  formatIQDCurrency(data.item.amount)
                }}</span>
              </template>
              <template #cell(discount)="data">
                <span>{{ formatIQDCurrency(data.item.discount || 0) }}</span>
              </template>
              <template #cell(created_at)="data">
                <span class="text-nowrap">{{
                  formatDateTime(data.item.created_at)
                }}</span>
              </template>
              <template #cell(approved_at)="data">
                <span class="text-nowrap">{{
                  formatDateTime(data.item.approved_at)
                }}</span>
              </template>
              <template #cell(created_by)="data">
                <span>{{
                  getNestedProperty(data.item, "created_by.name", "-")
                }}</span>
              </template>
              <template #cell(description)="data">
                <span>{{ data.item.description || "-" }}</span>
              </template>
              <template #cell(actions)="data">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="openDistributionModal(data.item)"
                >
                  <feather-icon
                    icon="ShareIcon"
                    size="14"
                  />
                  {{ $t("accounting.distribution") }}
                </b-button>
              </template>
            </b-table>

            <!-- Card View -->
            <div
              v-if="!loading && viewMode === 'card'"
              class="invoice-cards-container"
            >
              <div
                v-if="invoicesData.length === 0"
                class="text-center py-5"
              >
                <p class="text-muted">
                  No matching invoices found
                </p>
              </div>
              <b-row v-else>
                <b-col
                  v-for="invoice in invoicesData"
                  :key="getInvoiceIdentifier(invoice) || invoice.id"
                  md="6"
                  lg="4"
                  cols="12"
                  class="compact-card-col"
                >
                  <b-card class="invoice-card h-100 compact-invoice-card">
                    <div
                      class="d-flex flex-column justify-content-between h-100"
                    >
                      <div>
                        <div
                          class="invoice-card-header compact-card-header d-flex justify-content-between align-items-start"
                        >
                          <h6
                            class="mb-0 font-weight-bold text-primary compact-invoice-number"
                          >
                            {{ invoice.invoice_no || "-" }}
                          </h6>
                          <b-form-checkbox
                            :checked="isInvoiceSelected(invoice)"
                            @change="
                              (checked) =>
                                toggleInvoiceSelection(invoice, checked)
                            "
                          />
                        </div>

                        <div class="invoice-card-body compact-card-body">
                          <div class="invoice-info-item compact-info-item">
                            <span
                              class="info-label"
                            >{{ $t("common.type") }}:</span>
                            <span class="info-value">{{
                              formatInvoiceType(invoice.type)
                            }}</span>
                          </div>

                          <div class="invoice-info-item compact-info-item">
                            <span
                              class="info-label"
                            >{{ $t("reception.patient-name") }}:</span>
                            <span class="info-value">{{
                              getNestedProperty(
                                invoice,
                                "patient_card.patient.name",
                                "-"
                              )
                            }}</span>
                          </div>

                          <div class="invoice-info-item compact-info-item">
                            <span
                              class="info-label"
                            >{{ $t("hospital.service") }}:</span>
                            <span class="info-value">{{
                              getNestedProperty(invoice, "service.title") ||
                                getNestedProperty(invoice, "surgery.title") ||
                                "-"
                            }}</span>
                          </div>

                          <div
                            v-if="getDoctorName(invoice)"
                            class="invoice-info-item compact-info-item"
                          >
                            <span
                              class="info-label"
                            >{{ $t("common.doctor-name") }}:</span>
                            <span class="info-value">{{
                              getDoctorName(invoice)
                            }}</span>
                          </div>

                          <div class="invoice-info-item compact-info-item">
                            <span
                              class="info-label"
                            >{{ $t("common.created-at") }}:</span>
                            <span class="info-value">{{
                              formatDateTime(invoice.created_at)
                            }}</span>
                          </div>

                          <div
                            v-if="getNestedProperty(invoice, 'createdBy.name')"
                            class="invoice-info-item compact-info-item"
                          >
                            <span
                              class="info-label"
                            >{{ $t("common.created-by") }}:</span>
                            <span class="info-value">{{
                              getNestedProperty(invoice, "created_by.name", "-")
                            }}</span>
                          </div>

                          <div
                            v-if="invoice.description"
                            class="invoice-info-item compact-info-item"
                          >
                            <span
                              class="info-label"
                            >{{ $t("common.description") }}:</span>
                            <span class="info-value">{{
                              invoice.description
                            }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="invoice-card-footer compact-card-footer">
                        <div class="amount-section compact-amount-section">
                          <div
                            class="d-flex justify-content-between compact-amount-row"
                          >
                            <span
                              class="text-muted"
                            >{{ $t("common.amount") }}:</span>
                            <span class="font-weight-bold text-primary">{{
                              formatIQDCurrency(invoice.amount)
                            }}</span>
                          </div>
                          <div
                            class="d-flex justify-content-between compact-amount-row"
                          >
                            <span
                              class="text-muted"
                            >{{ $t("common.discount") }}:</span>
                            <span class="text-warning">{{
                              formatIQDCurrency(invoice.discount || 0)
                            }}</span>
                          </div>
                        </div>
                        <b-button
                          variant="primary"
                          size="sm"
                          block
                          class="compact-action-button"
                          @click="openDistributionModal(invoice)"
                        >
                          <feather-icon
                            icon="ShareIcon"
                            size="14"
                          />
                          {{ $t("accounting.distribution") }}
                        </b-button>
                      </div>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
            </div>

            <!-- Loading State -->
            <div
              v-if="loading"
              class="text-center compact-loading"
            >
              <b-spinner label="Loading..." />
              <p class="mt-1 text-muted compact-loading-text">
                {{ $t("common.loading") }}
              </p>
            </div>
          </b-col>
        </b-row>
      </div>
      <div
        v-if="dataCount > 0"
        class="compact-pagination"
      >
        <b-row class="compact-pagination-row">
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start compact-pagination-info"
          >
            <span
              class="text-muted compact-pagination-text"
            >{{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
              {{ $t("common.to") }} {{ Math.min(page * take, dataCount) }}
              {{ $t("common.of") }} {{ dataCount }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              size="sm"
              class="mb-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="16"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Distribution Modal -->
    <b-modal
      id="modal-distribution"
      :title="$t('accounting.distribution')"
      size="xl"
      :no-close-on-backdrop="true"
      centered
      @hide="closeDistributionModal"
    >
      <div
        v-if="loadingDistribution"
        class="text-center py-4"
      >
        <b-spinner label="Loading..." />
        <p class="mt-2">
          {{ $t("common.loading") }}
        </p>
      </div>
      <div
        v-else-if="distributionData && distributionData.data"
        class="invoice-details"
      >
        <!-- Invoice Basic Information -->
        <b-card class="mb-0 invoice-card-compact">
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("common.invoice-information") }}
          </h6>
          <b-row class="compact-row">
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("common.invoice-number") }}:</span>
                <span class="font-weight-bold compact-value">{{
                  distributionData.data.invoice_no || "-"
                }}</span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span class="compact-label">{{ $t("common.type") }}:</span>
                <span class="compact-value">{{
                  formatInvoiceType(distributionData.data.invoice_type)
                }}</span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span class="compact-label">{{ $t("common.date") }}:</span>
                <span class="compact-value">{{
                  formatDate(distributionData.data.invoice_date) || "-"
                }}</span>
              </div>
            </b-col>
            <!-- <b-col md="4" cols="6" class="compact-col">
              <div class="compact-field">
                <span class="compact-label">{{ $t("common.status") }}:</span>
                <b-badge
                  :variant="
                    distributionData.data.invoice_status === 'approved'
                      ? 'success'
                      : 'secondary'
                  "
                  class="compact-value"
                >
                  {{ distributionData.data.invoice_status || "-" }}
                </b-badge>
              </div>
            </b-col> -->
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.detailed-invoice") }}:</span>
                <span class="compact-value">{{
                  distributionData.data.detailed_invoice
                    ? $t("common.yes")
                    : $t("common.no")
                }}</span>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Patient Information -->
        <b-card
          v-if="distributionData.data.patient_info"
          class="mb-0 invoice-card-compact"
        >
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("reception.patient-information") }}
          </h6>
          <b-row class="compact-row">
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("reception.patient-name") }}:</span>
                <span class="compact-value">{{
                  distributionData.data.patient_info.patient_name || "-"
                }}</span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span class="compact-label">{{ $t("reception.phone") }}:</span>
                <span class="compact-value">{{
                  distributionData.data.patient_info.phone || "-"
                }}</span>
              </div>
            </b-col>
            <b-col
              v-if="distributionData.data.patient_info.age"
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span class="compact-label">{{ $t("common.age") }}:</span>
                <span class="compact-value">{{
                  distributionData.data.patient_info.age || "-"
                }}</span>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Summary -->
        <b-card class="mb-0 invoice-card-compact">
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("accounting.summary") }}
          </h6>
          <b-row class="compact-row">
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("common.total-amount") }}:</span>
                <span class="font-weight-bold text-primary compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.summary.total_amount
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span class="compact-label">{{ $t("common.discount") }}:</span>
                <span class="text-warning compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.summary.discount || 0
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("common.net-amount") }}:</span>
                <span class="font-weight-bold text-success compact-value">
                  {{
                    formatIQDCurrency(distributionData.data.summary.net_amount)
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.patient-pays") }}:</span>
                <span class="font-weight-bold text-info compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.summary.patient_pays
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.hospital-receives") }}:</span>
                <span class="font-weight-bold text-success compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.summary.hospital_receives
                    )
                  }}
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Breakdown -->
        <b-card class="mb-0 invoice-card-compact">
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("accounting.breakdown") }}
          </h6>
          <b-row class="compact-row">
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.hospital-services") }}:</span>
                <span class="font-weight-bold compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.breakdown.hospital_services.total
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.doctors") }}:</span>
                <span class="font-weight-bold compact-value">
                  {{ formatIQDCurrency(getDoctorsTotal()) }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.other-expenses") }}:</span>
                <span class="font-weight-bold compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.breakdown.other_expenses.discount ||
                        0
                    )
                  }}
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Hospital Services Items -->
        <b-card
          v-if="
            distributionData.data.breakdown.hospital_services.items &&
              distributionData.data.breakdown.hospital_services.items.length > 0
          "
          class="mb-0 invoice-card-compact"
        >
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("accounting.hospital-services-items") }}
          </h6>
          <b-table
            :items="distributionData.data.breakdown.hospital_services.items"
            :fields="hospitalServicesFields"
            responsive
            striped
            class="compact-table"
          >
            <template #cell(amount)="data">
              <span class="font-weight-bold">
                {{ formatIQDCurrency(data.item.amount) }}
              </span>
            </template>
          </b-table>
        </b-card>

        <!-- Doctors Breakdown -->
        <b-card
          v-if="
            distributionData.data.breakdown.doctors &&
              distributionData.data.breakdown.doctors.items &&
              distributionData.data.breakdown.doctors.items.length > 0
          "
          class="mb-0 invoice-card-compact"
        >
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("accounting.doctors-breakdown") }}
          </h6>
          <b-table
            :items="distributionData.data.breakdown.doctors.items"
            :fields="doctorsFields"
            responsive
            striped
            class="compact-table"
          >
            <template #cell(amount)="data">
              <span class="font-weight-bold">
                {{ formatIQDCurrency(data.item.amount) }}
              </span>
            </template>
            <template #cell(actions)="data">
              <b-button
                variant="primary"
                size="sm"
                @click="openEditDoctorAmountModal(data.item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                />
                {{ $t("common.edit") }}
              </b-button>
            </template>
          </b-table>
        </b-card>

        <!-- Distribution Summary -->
        <b-card class="mb-0 invoice-card-compact">
          <h6 class="mb-0 font-weight-bold compact-title">
            {{ $t("accounting.distribution-summary") }}
          </h6>
          <b-row class="compact-row">
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.to-hospital") }}:</span>
                <span class="font-weight-bold text-success compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.distribution.to_hospital
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.to-doctors") }}:</span>
                <span class="font-weight-bold text-primary compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.distribution.to_doctors
                    )
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              md="4"
              cols="6"
              class="compact-col"
            >
              <div class="compact-field">
                <span
                  class="compact-label"
                >{{ $t("accounting.total-distributed") }}:</span>
                <span class="font-weight-bold text-info compact-value">
                  {{
                    formatIQDCurrency(
                      distributionData.data.distribution.total_distributed
                    )
                  }}
                </span>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeDistributionModal"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="processingInvoice"
          @click="processInvoice"
        >
          <b-spinner
            v-if="processingInvoice"
            small
            class="mr-1"
          />
          {{ $t("accounting.process-distribution") }}
        </b-button>
      </template>
    </b-modal>

    <!-- Bulk Process Invoices Modal -->
    <b-modal
      id="modal-bulk-process-invoices"
      title="Bulk Process Invoices"
      size="lg"
      :no-close-on-backdrop="true"
      centered
      @hide="onBulkProcessModalHide"
    >
      <div>
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h6 class="mb-0">
            Selected Invoices
          </h6>
          <span class="text-muted">{{ selectedInvoiceIds.length }} selected</span>
        </div>

        <div
          v-if="selectedInvoicesForModal.length === 0"
          class="text-muted"
        >
          No invoices selected.
        </div>
        <ul
          v-else
          class="bulk-invoice-list"
        >
          <li
            v-for="invoice in selectedInvoicesForModal"
            :key="getInvoiceIdentifier(invoice)"
            class="bulk-invoice-list-item"
          >
            <span class="font-weight-bold mr-1">{{
              invoice.invoice_no || getInvoiceIdentifier(invoice)
            }}</span>
            <span class="text-muted">
              -
              {{
                getNestedProperty(invoice, "patient_card.patient.name", "-")
              }}
            </span>
          </li>
        </ul>

        <b-form-group class="mb-0 mt-1">
          <b-form-checkbox v-model="bulkMarkAsPaid">
            Mark all selected invoices as paid
          </b-form-checkbox>
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeBulkProcessModal"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="success"
          :disabled="processingBulkInvoices || selectedInvoiceIds.length === 0"
          @click="processBulkInvoices"
        >
          <b-spinner
            v-if="processingBulkInvoices"
            small
            class="mr-1"
          />
          Process Selected
        </b-button>
      </template>
    </b-modal>

    <!-- Edit Doctor Amount Modal -->
    <b-modal
      id="modal-edit-doctor-amount"
      :title="$t('accounting.edit-doctor-amount')"
      size="md"
      :no-close-on-backdrop="true"
      centered
      @hide="closeEditDoctorAmountModal"
    >
      <div v-if="selectedDoctorForEdit">
        <b-form-group :label="$t('common.doctor-name')">
          <span class="font-weight-bold">{{
            selectedDoctorForEdit.doctor_name || "-"
          }}</span>
        </b-form-group>
        <b-form-group :label="$t('common.role')">
          <span>{{ selectedDoctorForEdit.role || "-" }}</span>
        </b-form-group>
        <b-form-group :label="$t('accounting.current-amount')">
          <span class="font-weight-bold text-primary">
            {{ formatIQDCurrency(selectedDoctorForEdit.amount || 0) }}
          </span>
        </b-form-group>
        <b-form-group
          :label="$t('accounting.new-doctor-amount')"
          :invalid-feedback="$t('validation.required')"
        >
          <b-form-input
            v-model.number="doctorAmountForm.doctor_amount"
            type="number"
            :placeholder="$t('accounting.enter-doctor-amount')"
            min="0"
            step="0.01"
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeEditDoctorAmountModal"
        >
          {{ $t("common.cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="editingDoctorAmount || !doctorAmountForm.doctor_amount"
          @click="updateDoctorAmount"
        >
          <b-spinner
            v-if="editingDoctorAmount"
            small
            class="mr-1"
          />
          {{ $t("common.save") }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BSpinner,
  BIconSearch,
  BModal,
  BButtonGroup,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import vSelect from 'vue-select'
import { getNestedProperty } from '@/utils/nestedProperty'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AutoComplete from '@/components/common/AutoComplete.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BCard,
    BButton,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BSpinner,
    BModal,
    BIconSearch,
    BButtonGroup,
    Datepicker,
    vSelect,
    AutoComplete,
  },
  data() {
    return {
      tableColumns: [
        {
          key: 'select',
          label: '',
          sortable: false,
          thStyle: { width: '48px' },
          tdClass: 'compact-select-cell',
        },
        {
          key: 'invoice_no',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },
        {
          key: 'type',
          label: this.$t('common.type'),
          sortable: true,
        },
        {
          key: 'patientName',
          label: this.$t('reception.patient-name'),
          sortable: false,
        },
        {
          key: 'itemTitle',
          label: this.$t('hospital.service'),
          sortable: false,
        },
        {
          key: 'amount',
          label: this.$t('common.amount'),
          sortable: true,
        },
        {
          key: 'discount',
          label: this.$t('common.discount'),
          sortable: true,
        },
        {
          key: 'approved_at',
          label: this.$t('common.approved_at'),
          sortable: true,
        },
        {
          key: 'created_at',
          label: this.$t('common.created-at'),
          sortable: true,
        },
        {
          key: 'created_by',
          label: this.$t('common.created-by'),
          sortable: false,
        },
        {
          key: 'description',
          label: this.$t('common.description'),
          sortable: false,
        },
        {
          key: 'actions',
          label: this.$t('common.actions'),
          sortable: false,
        },
      ],
      invoicesData: [],
      page: 1,
      dataCount: 0,
      take: 10,
      loading: false,
      selectedDate: null,
      fromApprovedAt: null,
      toApprovedAt: null,
      selectedType: null,
      invoiceNumberFilter: '',
      patientNameFilter: '',
      selectedDoctor: null,
      doctorOptions: [],
      filterServiceType: null,
      serviceTypeOptions: [],
      viewMode: 'list', // 'list' or 'card'
      sums: null,
      invoiceTypeOptions: [],
      filterDebounce: null,
      isMounted: false,
      selectedInvoice: null,
      processingInvoice: false,
      selectedInvoiceIds: [],
      bulkMarkAsPaid: true,
      processingBulkInvoices: false,
      loadingDistribution: false,
      distributionData: null,
      invoiceItemsFields: [
        {
          key: 'title',
          label: this.$t('common.title'),
        },
        {
          key: 'amount',
          label: this.$t('common.amount'),
        },
        {
          key: 'quantity',
          label: this.$t('common.quantity'),
        },
      ],
      hospitalServicesFields: [
        {
          key: 'service_name',
          label: this.$t('common.name'),
        },
        {
          key: 'quantity',
          label: this.$t('common.quantity'),
        },
        {
          key: 'total_price',
          label: this.$t('common.amount'),
        },
        {
          key: 'itemable_type',
          label: this.$t('itemable type'),
        },
      ],
      doctorsFields: [
        {
          key: 'doctor_name',
          label: this.$t('common.doctor-name'),
        },
        {
          key: 'role',
          label: this.$t('common.role'),
        },
        {
          key: 'amount',
          label: this.$t('common.amount'),
        },
        {
          key: 'actions',
          label: this.$t('common.actions'),
        },
      ],
      selectedDoctorForEdit: null,
      editingDoctorAmount: false,
      doctorAmountForm: {
        doctor_amount: 0,
      },
    }
  },
  computed: {
    displayedInvoiceIds() {
      return this.invoicesData
        .map(invoice => this.getInvoiceIdentifier(invoice))
        .filter(Boolean)
    },
    allDisplayedSelected() {
      if (!this.displayedInvoiceIds.length) return false
      return this.displayedInvoiceIds.every(id => this.selectedInvoiceIds.includes(id))
    },
    someDisplayedSelected() {
      if (!this.displayedInvoiceIds.length) return false
      return this.selectedInvoiceIds.length > 0 && !this.allDisplayedSelected
    },
    selectedInvoicesForModal() {
      if (!this.selectedInvoiceIds.length) return []
      return this.invoicesData.filter(invoice => this.selectedInvoiceIds.includes(this.getInvoiceIdentifier(invoice)))
    },
    serviceTypeFilterOptions() {
      const options = [{ value: null, text: this.$t('common.all') }]
      this.serviceTypeOptions.forEach(st => {
        options.push({
          value: st.id,
          text: st.name,
        })
      })
      return options
    },
  },
  watch: {
    page() {
      this.getApprovedInvoices()
    },
    selectedDate() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
    selectedType() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
    fromApprovedAt() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
    toApprovedAt() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
    invoiceNumberFilter() {
      this.handleFilterChange()
    },
    patientNameFilter() {
      this.handleFilterChange()
    },
    selectedDoctor() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
    filterServiceType() {
      if (this.isMounted) {
        this.handleFilterChange()
      }
    },
  },
  mounted() {
    this.initializeInvoiceTypeOptions()
    this.loadDoctorOptions()
    this.loadServiceTypes()
    this.getApprovedInvoices()
    this.isMounted = true
  },
  beforeDestroy() {
    if (this.filterDebounce) {
      clearTimeout(this.filterDebounce)
    }
  },
  methods: {
    getNestedProperty,
    handleFilterChange() {
      if (!this.isMounted) return
      if (this.filterDebounce) {
        clearTimeout(this.filterDebounce)
      }
      this.filterDebounce = setTimeout(() => {
        this.getApprovedInvoices()
      }, 400)
    },
    initializeInvoiceTypeOptions() {
      const types = [
        'invoice_out_clinic',
        'invoice_nicu',
        'invoice_patient_card_test',
        'refund',
        'invoice_insurance_surgery',
        'invoice_emergency',
      ]
      this.invoiceTypeOptions = [
        { value: null, label: 'All Types' },
        ...types.map(type => ({
          value: type,
          label: this.formatInvoiceType(type),
        })),
      ]
    },
    async loadDoctorOptions() {
      // this.fetchDoctorOptions();
    },
    async loadServiceTypes() {
      try {
        await this.getList('serviceTypes?page=1&take=50&sort=-id')
        const rows = Array.isArray(this.all_data?.data) ? this.all_data.data : []
        this.serviceTypeOptions = rows.map(r => ({
          id: r.id,
          name: r.name,
        }))
      } catch (e) {
        this.serviceTypeOptions = []
      }
    },
    getInvoiceServiceTitle(invoice) {
      return this.getNestedProperty(invoice, 'service.title', null)
    },
    getInvoiceServiceTypeName(invoice) {
      return this.getNestedProperty(invoice, 'service.type.name', null)
    },
    getDoctorsTotal() {
      if (
        !this.distributionData
        || !this.distributionData.data
        || !this.distributionData.data.breakdown
      ) {
        return 0
      }

      const { doctors } = this.distributionData.data.breakdown

      // If doctors.total exists, use it
      if (doctors && doctors.total !== undefined) {
        return doctors.total
      }

      // Otherwise calculate from doctors.items array
      if (doctors && doctors.items && Array.isArray(doctors.items)) {
        return doctors.items.reduce(
          (sum, doc) => sum + (parseFloat(doc.amount) || 0),
          0,
        )
      }

      // Fallback for old API structure where doctors is directly an array
      if (doctors && Array.isArray(doctors)) {
        return doctors.reduce(
          (sum, doc) => sum + (parseFloat(doc.amount) || 0),
          0,
        )
      }

      return 0
    },
    formatDate(wantedDate) {
      if (!wantedDate) return '-'
      const date = new Date(wantedDate)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      return date.toLocaleString()
    },
    formatIQDCurrency(value) {
      if (!value) return '0 IQD'
      return `${new Intl.NumberFormat('en-US').format(value)} IQD`
    },
    getDoctorName(invoice) {
      if (!invoice) return null
      if (invoice.doctor && invoice.doctor.name) return invoice.doctor.name
      if (
        invoice.doctors
        && Array.isArray(invoice.doctors)
        && invoice.doctors.length > 0
      ) {
        return invoice.doctors.map(doc => doc.name).join(', ')
      }
      return null
    },
    formatInvoiceType(type) {
      if (!type) return '-'
      // Format the key: snake_case to Title Case
      const formattedKey = type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      // Map invoice types to translation keys
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      const translationKey = typeMap[type]
      if (translationKey) {
        const translated = this.$t(translationKey)
        // If translation exists and is different from key, return both
        if (translated && translated !== translationKey) {
          return `${translated} (${formattedKey})`
        }
      }
      // Fallback: return formatted key only
      return formattedKey
    },
    async getApprovedInvoices() {
      this.loading = true
      let url = `/invoices/approved?page=${this.page}&take=${this.take}&sort=-id&include=service,surgery,patientCard,doctor,invoiceItems,patientCardIncubator,patientCardIncubator.incubator,patientCardTest,patientCardTest.test,createdBy`

      if (this.selectedDate) {
        const formattedDate = this.formatDate(this.selectedDate)
        url += `&filter[date][eq]=${formattedDate}`
      }
      if (this.fromApprovedAt) {
        const fromApprovedAt = this.formatDate(this.fromApprovedAt)
        url += `&approved_at_from=${fromApprovedAt}`
      }
      if (this.toApprovedAt) {
        const toApprovedAt = this.formatDate(this.toApprovedAt)
        url += `&approved_at_to=${toApprovedAt}`
      }

      if (this.selectedType) {
        url += `&filter[type][eq]=${this.selectedType}`
      }

      const trimmedInvoiceNumber = this.invoiceNumberFilter && this.invoiceNumberFilter.trim()
      if (trimmedInvoiceNumber) {
        url += `&filter[invoice_no][eq]=${encodeURIComponent(
          trimmedInvoiceNumber,
        )}`
      }

      const trimmedPatientName = this.patientNameFilter && this.patientNameFilter.trim()
      if (trimmedPatientName) {
        const patientCardIds = await this.fetchPatientCardIdsByName(
          trimmedPatientName,
        )
        if (patientCardIds.length === 0) {
          this.invoicesData = []
          this.dataCount = 0
          this.sums = null
          this.loading = false
          return
        }
        url += `&filter[patient_card_id]=${patientCardIds.join(',')}`
      }

      if (this.selectedDoctor) {
        url += `&filter[doctor_id][eq]=${this.selectedDoctor}`
      }
      if (this.filterServiceType) {
        url += `&filter[service_type_id]=${this.filterServiceType}`
      }

      await this.getList(url)

      // Handle the response structure: { data: [], pagination: {}, sums: {} }
      if (this.all_data) {
        // Handle data
        if (Array.isArray(this.all_data.data)) {
          this.invoicesData = this.all_data.data
        } else if (Array.isArray(this.all_data)) {
          this.invoicesData = this.all_data
        } else {
          this.invoicesData = []
        }

        // Handle pagination
        if (this.all_data.pagination) {
          this.dataCount = this.all_data.pagination.total || 0
          // Don't overwrite this.page - let the user's selection persist
          // Only update take if it's different
          if (this.all_data.pagination.per_page) {
            this.take = this.all_data.pagination.per_page
          }
        } else if (this.all_data.total) {
          this.dataCount = this.all_data.total
        } else {
          this.dataCount = this.invoicesData.length
        }

        // Handle sums
        if (this.all_data.sums) {
          this.sums = this.all_data.sums
        } else {
          this.sums = null
        }
      } else {
        this.invoicesData = []
        this.dataCount = 0
        this.sums = null
      }

      this.selectedInvoiceIds = []
      this.loading = false
    },
    getInvoiceIdentifier(invoice) {
      if (!invoice) return null
      return invoice.uuid || invoice.invoice_uuid || invoice.id || null
    },
    isInvoiceSelected(invoice) {
      const invoiceId = this.getInvoiceIdentifier(invoice)
      if (!invoiceId) return false
      return this.selectedInvoiceIds.includes(invoiceId)
    },
    toggleInvoiceSelection(invoice, checked) {
      const invoiceId = this.getInvoiceIdentifier(invoice)
      if (!invoiceId) return

      if (checked) {
        if (!this.selectedInvoiceIds.includes(invoiceId)) {
          this.selectedInvoiceIds.push(invoiceId)
        }
      } else {
        this.selectedInvoiceIds = this.selectedInvoiceIds.filter(
          id => id !== invoiceId,
        )
      }
    },
    toggleSelectAllDisplayed(checked) {
      if (checked) {
        this.selectedInvoiceIds = [...this.displayedInvoiceIds]
        return
      }
      this.selectedInvoiceIds = []
    },
    openBulkProcessModal() {
      if (!this.selectedInvoiceIds.length) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: 'Please select at least one invoice.',
          },
        })
        return
      }
      this.$bvModal.show('modal-bulk-process-invoices')
    },
    closeBulkProcessModal() {
      this.$bvModal.hide('modal-bulk-process-invoices')
    },
    onBulkProcessModalHide() {
      this.processingBulkInvoices = false
    },
    async processBulkInvoices() {
      if (!this.selectedInvoiceIds.length) return
      this.processingBulkInvoices = true

      try {
        await this.$http.post('/invoices/bulk-process', {
          invoices: this.selectedInvoiceIds,
          mark_as_paid: this.bulkMarkAsPaid,
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: 'Invoices processed successfully.',
          },
        })

        this.closeBulkProcessModal()
        await this.getApprovedInvoices()
      } catch (error) {
        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || 'Failed to process selected invoices.'

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.processingBulkInvoices = false
      }
    },
    async fetchPatientCardIdsByName(name) {
      try {
        const encodedName = encodeURIComponent(name)
        const response = await this.$http.get(
          `/patientCard?filter[patient.name]=${encodedName}&take=1000`,
        )
        const patientCards = Array.isArray(response.data?.data)
          ? response.data.data
          : []
        return patientCards.map(card => card.id).filter(Boolean)
      } catch (error) {
        return []
      }
    },
    async openDistributionModal(invoice) {
      this.selectedInvoice = invoice
      this.distributionData = null
      this.loadingDistribution = true
      this.$bvModal.show('modal-distribution')

      try {
        const response = await this.$http.get(
          `/invoices/${invoice.id}/distribution`,
        )
        this.distributionData = response.data
      } catch (error) {
        console.error('Error fetching distribution data:', error)
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text:
              error.response?.data?.message
              || error.response?.data?.error
              || this.$t('accounting.error-loading-distribution'),
          },
        })
      } finally {
        this.loadingDistribution = false
      }
    },
    async refreshDistributionData() {
      if (!this.selectedInvoice || !this.selectedInvoice.id) return

      this.loadingDistribution = true
      try {
        const response = await this.$http.get(
          `/invoices/${this.selectedInvoice.id}/distribution`,
        )
        this.distributionData = response.data
      } catch (error) {
        console.error('Error refreshing distribution data:', error)
      } finally {
        this.loadingDistribution = false
      }
    },
    closeDistributionModal() {
      this.selectedInvoice = null
      this.distributionData = null
      this.$bvModal.hide('modal-distribution')
    },
    async processInvoice() {
      if (!this.selectedInvoice || !this.selectedInvoice.id) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('accounting.invoice-not-selected'),
          },
        })
        return
      }

      this.processingInvoice = true

      try {
        await this.$http.post(`/invoices/${this.selectedInvoice.id}/process`)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('accounting.invoice-processed-successfully'),
          },
        })

        // Close modal and refresh the list
        this.closeDistributionModal()
        await this.getApprovedInvoices()
      } catch (error) {
        console.error('Error processing invoice:', error)
        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || this.$t('accounting.error-processing-invoice')

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.processingInvoice = false
      }
    },
    openEditDoctorAmountModal(doctor) {
      this.selectedDoctorForEdit = doctor
      this.doctorAmountForm.doctor_amount = parseFloat(doctor.amount) || 0
      this.$bvModal.show('modal-edit-doctor-amount')
    },
    closeEditDoctorAmountModal() {
      this.selectedDoctorForEdit = null
      this.doctorAmountForm.doctor_amount = 0
      this.$bvModal.hide('modal-edit-doctor-amount')
    },
    async updateDoctorAmount() {
      if (
        !this.selectedInvoice
        || !this.selectedInvoice.id
        || !this.selectedDoctorForEdit
      ) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('accounting.doctor-not-selected'),
          },
        })
        return
      }

      if (
        !this.doctorAmountForm.doctor_amount
        || this.doctorAmountForm.doctor_amount <= 0
      ) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: this.$t('accounting.invalid-doctor-amount'),
          },
        })
        return
      }

      this.editingDoctorAmount = true

      try {
        const payload = {
          doctor_amount: parseFloat(this.doctorAmountForm.doctor_amount),
        }

        // Include doctor_id if available
        if (this.selectedDoctorForEdit.doctor_id) {
          payload.doctor_id = this.selectedDoctorForEdit.doctor_id
        } else if (this.selectedDoctorForEdit.id) {
          payload.doctor_id = this.selectedDoctorForEdit.id
        }

        await this.$http.put(
          `/invoices/${this.selectedInvoice.id}/update-doctor-amount`,
          payload,
        )

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success'),
            variant: 'success',
            text: this.$t('accounting.doctor-amount-updated-successfully'),
          },
        })

        // Close modal and refresh distribution data
        this.closeEditDoctorAmountModal()
        await this.refreshDistributionData()
      } catch (error) {
        console.error('Error updating doctor amount:', error)
        const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || this.$t('accounting.error-updating-doctor-amount')

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.error'),
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.editingDoctorAmount = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.main-padding {
  padding: 1rem;
}
.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

// Compact modal styles
.invoice-details {
  .invoice-card-compact {
    margin-bottom: 0.25rem !important;
    padding: 0.5rem !important;
    border: 1px solid #e0e0e0;

    .compact-title {
      margin-bottom: 0.25rem !important;
      font-size: 18px;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid #e0e0e0;
    }
  }

  .compact-row {
    margin-bottom: 0 !important;
    margin-left: -0.25rem;
    margin-right: -0.25rem;
  }

  .compact-col {
    padding: 0.15rem 0.25rem !important;
  }

  .compact-field {
    display: flex;
    align-items: baseline;
    margin-bottom: 0.25rem;
    font-size: 18px;
    line-height: 1.3;

    .compact-label {
      font-weight: 600;
      margin-right: 0.5rem;
      min-width: fit-content;
      color: #666;
      font-size: 14px;
    }

    .compact-value {
      flex: 1;
      word-break: break-word;
      font-size: 14px;
    }
  }

  .compact-table {
    margin-top: 0.25rem;

    ::v-deep th,
    ::v-deep td {
      padding: 0.35rem 0.5rem !important;
      font-size: 0.75rem;
    }

    ::v-deep thead th {
      font-size: 0.7rem;
      font-weight: 600;
      padding: 0.4rem 0.5rem !important;
    }
  }
}

// Compact styles - no margins/padding
.compact-card {
  margin: 0;
  border: none;
}

.compact-container {
  padding: 0.5rem;
}

.compact-row {
  margin: 0;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.compact-header {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.compact-title {
  margin: 0;
}

.compact-filters {
  margin-bottom: 0.5rem;
}

.compact-col {
  padding: 0.25rem;
}

.compact-form-group {
  margin-bottom: 0.5rem;

  ::v-deep label {
    margin-bottom: 0.25rem;
  }
}

.compact-button {
  margin: 0;
}

.compact-summary {
  margin: 0.5rem 0;
}

.compact-summary-card {
  padding: 0.5rem;
  margin: 0.25rem;
  border: 1px solid #e0e0e0;
}

.compact-summary-label {
  margin-bottom: 0.25rem;
}

.compact-summary-value {
  margin: 0;
}

.compact-content-row {
  margin: 0.5rem 0;
}

// View toggle buttons
.view-toggle-buttons {
  display: flex;
  align-items: center;
}

// Compact table
.compact-table {
  margin: 0;

  ::v-deep th,
  ::v-deep td {
    padding: 0.5rem 0.75rem;
  }

  ::v-deep thead th {
    font-weight: 600;
    padding: 0.5rem 0.75rem;
  }
}

// Invoice card view styles - compact
.invoice-cards-container {
  min-height: 150px;
  margin: 0;
}

.compact-card-col {
  padding: 0.25rem;
  margin: 0;
}

.compact-invoice-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin: 0;
  padding: 0.5rem;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .compact-card-header {
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .compact-invoice-number {
    margin: 0;
  }

  .compact-card-body {
    padding: 0.5rem 0;
    flex: 1;
  }

  .compact-card-footer {
    border-top: 1px solid #f0f0f0;
    padding-top: 0.5rem;
    margin-top: auto;
  }

  .compact-info-item {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
    gap: 0.5rem;
    &:last-child {
      margin-bottom: 0;
    }

    .info-label {
      font-weight: 600;
      color: #666;
      margin-bottom: 0.15rem;
    }

    .info-value {
      color: #333;
      word-break: break-word;
    }
  }

  .compact-amount-section {
    background-color: #f8f9fa;
    padding: 0.5rem;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }

  .compact-amount-row {
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .compact-action-button {
    padding: 0.375rem;
  }
}

.compact-loading {
  padding: 1rem 0;
}

.compact-loading-text {
  margin: 0.5rem 0 0 0;
}

.compact-pagination {
  padding: 0.5rem;
  margin: 0;
}

.compact-pagination-row {
  margin: 0;
}

.compact-pagination-info {
  padding: 0.25rem;
}

.compact-pagination-controls {
  ::v-deep .page-link {
    padding: 0.25rem 0.5rem;
  }

  ::v-deep .page-item {
    margin: 0 0.1rem;
  }
}

.compact-select-cell {
  width: 48px;
}

.bulk-invoice-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
}

.bulk-invoice-list-item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f1f3f5;
}

.bulk-invoice-list-item:last-child {
  border-bottom: none;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
